import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AlertTriangle } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { useTranslation } from 'react-i18next'
import { type Space } from '@/types/spaces'

interface DeleteSpaceDialogProps {
  space: Space
  open: boolean
  onOpenChange: (open: boolean) => void
  onSpaceDeleted?: () => void
}

export const DeleteSpaceDialog = ({
  space,
  open,
  onOpenChange,
  onSpaceDeleted
}: DeleteSpaceDialogProps) => {
  const { t } = useTranslation('common')
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setError('')
      setIsLoading(false)
    }
  }, [open])

  const handleDelete = async () => {
    setIsLoading(true)
    setError('')

    try {
      await sdk.spaces.delete(space.id)
      onOpenChange(false)
      onSpaceDeleted?.()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('errors.deleteFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <DialogTitle>{t('spaces.deleteSpace')}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="pt-2">
            {t('spaces.deleteConfirmation', { title: space.title })}
          </DialogDescription>
        </DialogHeader>

        {space.module === 'courses' && (
          <div role="alert" className="p-3 text-sm text-amber-800 bg-amber-50 rounded-lg">
            {t('spaces.deleteCoursesWarning')}
          </div>
        )}

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? t('actions.deleting') : t('actions.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
